/**************
 Cart bundle upgrade card (theme-mini-cart section, "Bundle upgrade card" settings)
 Shows one "Make it a bundle" card in the cart drawer when a bundle beats what is in the cart.

 Each rule says: bundle (to) = cart product (from) + added product (add). The added product can itself
 be a bundle (e.g. FlowBoots + Full Body Relief = Ultimate), so a bundle's parts are worked out from all rules.

 For each bundle not already in the cart, the cart lines that are parts of it are matched (one unit each):
 - parts missing, bundle costs more: "Add PULSE for $100 more" (upgrade).
 - nothing missing, or bundle costs less than the matched lines: "Switch to the bundle and save $65" (switch).
 The best offer wins: fewest missing parts, then most cart lines covered. An upgrade can start from any rule's
 cart product, so a ROLL-only cart is offered Ultimate when Full Body Relief doesn't save at least the minimum.
 Upgrade / switch = add the bundle (same colour where it exists), then take one unit off each matched line.
 Prices come from the cart (after automatic discounts), so an offer only shows when it really saves money.
 ***************/

(function () {
    const configEl = document.querySelector("#cartBundleUpgradeJSON");
    if (!configEl) {
        return;
    }
    let config;
    try {
        config = JSON.parse(configEl.textContent);
    } catch (e) {
        return;
    }
    if (!config.enabled || !config.rules || config.rules.length === 0) {
        return;
    }

    const body = document.querySelector("#miniCartCenterBody");
    if (!body) {
        return;
    }

    // bundles in rule order, with their direct parts, variants and name
    const bundles = [];
    const bundleById = {};
    // what we know about each added product: label, list price, image
    const productInfo = {};
    config.rules.forEach(function (rule) {
        let bundle = bundleById[rule.to.id];
        if (!bundle) {
            bundle = {id: rule.to.id, name: rule.to.name, image: rule.to.image, variants: rule.to.variants, parts: [], from: []};
            bundleById[rule.to.id] = bundle;
            bundles.push(bundle);
        }
        [rule.from, rule.add.id].forEach(function (id) {
            if (bundle.parts.indexOf(id) === -1) {
                bundle.parts.push(id);
            }
        });
        if (bundle.from.indexOf(rule.from) === -1) {
            bundle.from.push(rule.from);
        }
        if (!productInfo[rule.add.id]) {
            productInfo[rule.add.id] = rule.add;
        }
    });

    // products an upgrade can start from: every rule's cart product
    const startIds = [];
    config.rules.forEach(function (rule) {
        if (startIds.indexOf(rule.from) === -1) {
            startIds.push(rule.from);
        }
    });

    // single products a product stands for: a bundle expands to its parts, anything else is itself
    function leaves(id, seen) {
        const bundle = bundleById[id];
        if (!bundle) {
            return [id];
        }
        seen = seen || [];
        if (seen.indexOf(id) !== -1) {
            return [];
        }
        seen.push(id);
        const out = [];
        bundle.parts.forEach(function (part) {
            leaves(part, seen).forEach(function (leaf) {
                if (out.indexOf(leaf) === -1) {
                    out.push(leaf);
                }
            });
        });
        return out;
    }

    function sameSet(a, b) {
        return a.length === b.length && a.every(function (x) { return b.indexOf(x) !== -1; });
    }

    // label, price and image for the missing parts: a configured product that is exactly those parts
    // (e.g. Full Body Relief for ROLL + PULSE), or the single parts added up, whichever is cheaper
    function describeMissing(missing) {
        let exact = null;
        Object.keys(productInfo).forEach(function (id) {
            if (sameSet(leaves(Number(id)), missing)) {
                exact = productInfo[id];
            }
        });
        let sum = null;
        if (missing.every(function (id) { return productInfo[id]; })) {
            sum = {
                label: missing.map(function (id) { return productInfo[id].label; }).join(" + "),
                price: missing.reduce(function (total, id) { return total + productInfo[id].price; }, 0),
                image: productInfo[missing[0]].image,
                // one image per added product (shown side by side when more than one is added)
                images: missing.map(function (id) { return productInfo[id].image; }).filter(Boolean)
            };
            // one picture of exactly these products (theme setting "Combined image"), instead of one image each
            (config.combos || []).forEach(function (combo) {
                if (combo.image && sameSet(combo.ids, missing)) {
                    sum.image = combo.image;
                    sum.images = [combo.image];
                    sum.combo = true;
                }
            });
        }
        if (exact && (!sum || exact.price <= sum.price)) {
            return exact;
        }
        return sum;
    }

    function money(cents, currency) {
        const whole = cents % 100 === 0;
        try {
            return new Intl.NumberFormat(currency === "AUD" ? "en-AU" : "en-US", {
                style: "currency",
                currency: currency || "USD",
                minimumFractionDigits: whole ? 0 : 2,
                maximumFractionDigits: whole ? 0 : 2
            }).format(cents / 100);
        } catch (e) {
            return "$" + (cents / 100).toFixed(whole ? 0 : 2);
        }
    }

    function track(name) {
        if (typeof window.clarity === "function") {
            window.clarity("event", name);
        }
    }

    function escapeHTML(text) {
        const div = document.createElement("div");
        div.textContent = text == null ? "" : String(text);
        return div.innerHTML;
    }

    // "FlowBoots", "ROLL + PULSE" read as plural
    function isPlural(label) {
        return /[^s]s$|\+|&| and /i.test(label || "");
    }

    function unitPrice(line) {
        return Math.round(line.final_line_price / line.quantity);
    }

    // the bundle variant in the same colour as one of the matched lines (first match wins), else the first one in stock
    function pickVariant(bundle, lines) {
        const available = bundle.variants.filter(function (v) { return v.available; });
        if (available.length === 0) {
            return null;
        }
        for (const line of lines) {
            const colour = (line.variant_options && line.variant_options[0]) || line.variant_title || "";
            const match = available.find(function (v) { return v.option1 === colour; });
            if (match) {
                return match;
            }
        }
        return available[0];
    }

    function findOffer(cart) {
        const paidLines = cart.items.filter(function (i) { return i.final_line_price > 0; });
        let best = null;
        bundles.forEach(function (bundle) {
            if (cart.items.some(function (i) { return i.product_id === bundle.id; })) {
                return;
            }
            const wanted = leaves(bundle.id);
            const covered = [];
            const lines = [];
            paidLines.forEach(function (line) {
                const lineLeaves = leaves(line.product_id);
                const fits = lineLeaves.every(function (leaf) {
                    return wanted.indexOf(leaf) !== -1 && covered.indexOf(leaf) === -1;
                });
                if (fits) {
                    lines.push(line);
                    lineLeaves.forEach(function (leaf) { covered.push(leaf); });
                }
            });
            if (lines.length === 0) {
                return;
            }
            const missing = wanted.filter(function (leaf) { return covered.indexOf(leaf) === -1; });
            // an upgrade needs a starting product in the cart (any rule's cart product, e.g. ROLL can step up to
            // Ultimate when Full Body Relief doesn't save enough); a full switch doesn't
            if (missing.length > 0 && !lines.some(function (l) { return startIds.indexOf(l.product_id) !== -1; })) {
                return;
            }
            const variant = pickVariant(bundle, lines);
            if (!variant) {
                return;
            }
            const separate = lines.reduce(function (total, l) { return total + unitPrice(l); }, 0);
            const add = missing.length > 0 ? describeMissing(missing) : null;
            if (missing.length > 0 && !add) {
                return;
            }
            const delta = variant.price - separate;
            let offer;
            if (delta < 0 || missing.length === 0) {
                // bundle costs less than what is in the cart
                if (-delta < config.minSaving) {
                    return;
                }
                offer = {mode: "switch", saving: -delta};
            } else {
                // bundle costs more, but less than buying the missing parts
                if (delta === 0 || add.price - delta < config.minSaving) {
                    return;
                }
                offer = {mode: "add", delta: delta};
            }
            offer.bundle = bundle;
            offer.variant = variant;
            offer.lines = lines;
            offer.separate = separate;
            offer.add = add;
            offer.missingCount = missing.length;
            if (!best || offer.missingCount < best.missingCount ||
                (offer.missingCount === best.missingCount && offer.lines.length > best.lines.length)) {
                best = offer;
            }
        });
        return best;
    }

    let rendering = false;
    let lastSignature = "";

    // the "You may also like" card for the same bundle would add it next to the original lines, so hide it while this card shows
    function hideSameBundleUpsell(bundleId) {
        document.querySelectorAll(".miniCartCenterUpsellsItem[data-cbu-hidden]").forEach(function (el) {
            el.style.display = "";
            el.removeAttribute("data-cbu-hidden");
        });
        if (!bundleId) {
            return;
        }
        document.querySelectorAll('.miniCartCenterUpsellsItem[data-product="' + bundleId + '"]').forEach(function (el) {
            el.style.display = "none";
            el.setAttribute("data-cbu-hidden", "");
        });
    }

    function cardHTML(offer, currency) {
        const bundle = offer.bundle;
        const variant = offer.variant;
        const add = offer.add;
        const showColour = bundle.variants.length > 1 && variant.title && variant.title !== "Default Title";
        const colourNote = showColour ? " Colour: " + escapeHTML(variant.title) + "." : "";
        let image;
        let title;
        let note;
        let button;
        if (offer.mode === "switch") {
            // the bundle in the colour it will be added in
            image = variant.image || bundle.image || (add && add.image);
            title = "Switch to the " + escapeHTML(bundle.name) + " and save " + money(offer.saving, currency);
            note = "Bought separately: " + money(offer.separate, currency) + ". As the bundle: " + money(variant.price, currency) +
                (add ? ", " + escapeHTML(add.label) + " included" : "") + "." + colourNote;
            button = "Switch and save " + money(offer.saving, currency);
        } else {
            image = add.image || bundle.image;
            title = "Add " + escapeHTML(add.label) + " for " + money(offer.delta, currency) + " more";
            note = escapeHTML(bundle.name) + " " + money(variant.price, currency) + ". " + escapeHTML(add.label) +
                (isPlural(add.label) ? " on their own are " : " on its own is ") + money(add.price, currency) + "." + colourNote;
            button = "Upgrade for " + money(offer.delta, currency);
        }
        const pair = offer.mode !== "switch" && add && add.images && add.images.length > 1 ? add.images.slice(0, 2) : null;
        const imageHTML = pair
            ? '<span class="cartUpgradeImgs">' + pair.map(function (src) { return '<img class="cartUpgradeImg" src="' + escapeHTML(src) + '" alt="" width="56" height="56" loading="lazy">'; }).join("") + '</span>'
            : (image ? '<img class="cartUpgradeImg' + (offer.mode !== "switch" && add && add.combo ? ' cartUpgradeImg--combo' : '') + '" src="' + escapeHTML(image) + '" alt="" width="56" height="56" loading="lazy">' : '');
        return '<div class="cartUpgradeRow">' +
                imageHTML +
                '<div class="cartUpgradeText">' +
                    '<span class="cartUpgradeEyebrow">' + escapeHTML(config.title) + '</span>' +
                    '<span class="cartUpgradeTitle">' + title + '</span>' +
                    '<span class="cartUpgradeNote">' + note + '</span>' +
                '</div>' +
            '</div>' +
            '<button type="button" class="cartUpgradeBtn button">' + button + '</button>';
    }

    async function render() {
        if (rendering) {
            return;
        }
        rendering = true;
        try {
            const cart = await fetch("/cart.js", {headers: {"Accept": "application/json"}}).then(function (r) { return r.json(); });
            const offer = findOffer(cart);
            const signature = offer ? [offer.mode, offer.variant.id, offer.delta || offer.saving].concat(offer.lines.map(function (l) { return l.key; })).join("|") : "";
            const existing = body.querySelector(".cartUpgrade");

            if (!offer) {
                if (existing) {
                    existing.remove();
                }
                hideSameBundleUpsell(null);
                lastSignature = "";
                return;
            }
            hideSameBundleUpsell(offer.bundle.id);
            // card goes under the matched line that sits lowest in the drawer
            let anchor = null;
            body.querySelectorAll(".miniCartCenterBodyItem[key]").forEach(function (el) {
                if (offer.lines.some(function (l) { return l.key === el.getAttribute("key"); })) {
                    anchor = el;
                }
            });
            if (!anchor) {
                return;
            }
            if (existing && signature === lastSignature && existing.previousElementSibling === anchor) {
                return;
            }
            if (existing) {
                existing.remove();
            }

            const isSwitch = offer.mode === "switch";
            const card = document.createElement("div");
            card.className = "cartUpgrade";
            card.innerHTML = cardHTML(offer, cart.currency);
            anchor.insertAdjacentElement("afterend", card);
            lastSignature = signature;
            track(isSwitch ? "cart_bundle_switch_view" : "cart_bundle_upgrade_view");

            card.querySelector(".cartUpgradeBtn").addEventListener("click", async function () {
                const button = this;
                if (button.classList.contains("loading")) {
                    return;
                }
                button.classList.add("loading");
                button.disabled = true;
                track(isSwitch ? "cart_bundle_switch_click" : "cart_bundle_upgrade_click");
                try {
                    // add the bundle first, so nothing is lost if the add fails
                    const added = await fetch("/cart/add.js", {
                        method: "POST",
                        headers: {"Content-Type": "application/json", "Accept": "application/json"},
                        body: JSON.stringify({items: [{id: offer.variant.id, quantity: 1, properties: {_upsell: isSwitch ? "cart-bundle-switch" : "cart-bundle-upgrade"}}]})
                    });
                    if (!added.ok) {
                        throw new Error("add failed");
                    }
                    const updates = {};
                    offer.lines.forEach(function (l) {
                        updates[l.key] = l.quantity - 1;
                    });
                    await fetch("/cart/update.js", {
                        method: "POST",
                        headers: {"Content-Type": "application/json", "Accept": "application/json"},
                        body: JSON.stringify({updates: updates})
                    });
                } catch (e) {
                    button.classList.remove("loading");
                    button.disabled = false;
                    return;
                }
                lastSignature = "";
                if (typeof window.openCart === "function") {
                    await window.openCart();
                } else {
                    render();
                }
            });
        } catch (e) {
            // leave the drawer as it is
        } finally {
            rendering = false;
        }
    }

    // re-render after the drawer draws its lines; ignore changes made by this card itself
    let timer = null;
    new MutationObserver(function (mutations) {
        const onlyCard = mutations.every(function (m) {
            return [...m.addedNodes, ...m.removedNodes].every(function (n) {
                return n.nodeType === 1 && n.classList.contains("cartUpgrade");
            });
        });
        if (onlyCard) {
            return;
        }
        clearTimeout(timer);
        timer = setTimeout(render, 150);
    }).observe(body, {childList: true});
}());
